
import {queryFormSubmitElements} from "../common/form";
import {withTimeout} from "../common/promise";
import type {SlAlert} from "@shoelace-style/shoelace/shoelace";

const TURNSTILE_WIDGET_SELECTOR = '.cf-turnstile';
const TURNSTILE_ERROR_ALERT_SELECTOR = 'sl-alert.cf-turnstile-error-alert';
const TURNSTILE_LOAD_TIMEOUT_MS = 30000;

export const TURNSTILE_RESPONSE_FIELD = 'cf-turnstile-response';

/**
 * Registriert ein Turnstile-Widget für ein Standard-HTML-Formular,
 * das über den Standard-Form-Submit (Page-Load) abgeschickt wird.
 */
export function registerTurnstileForSimpleForm(form: HTMLFormElement): FormRegistration {
    return FormRegistration.create(form, true);
}

/**
 * Registriert ein Turnstile-Widget für ein Formular, das nicht über
 * über den Standard-Form-Submit abgeschickt wird, sondern z.B. mit
 * AJAX im Submit-Event-Listener.
 */
export function registerTurnstileForCustomSubmitForm(form: HTMLFormElement): FormRegistration {
    return FormRegistration.create(form, false);
}

enum InitState {
    Uninitialized = 'uninitialized',
    Initializing = 'initializing',
    Initialized = 'initialized',
    Destroying = 'destroying',
    Poisoned = 'poisoned',
}

export function hasTurnstileWidget(element: HTMLElement): boolean {
    return element.querySelector(TURNSTILE_WIDGET_SELECTOR) !== null;
}

export class FormRegistration {
    private readonly widget: HTMLElement;
    private readonly errorAlert: SlAlert|null;
    private readonly listeners: ListenerRegistration[] = [];
    private turnstile: Turnstile|null = null;
    private initPromise: Promise<void>|null = null;
    private destroyPromise: Promise<void>|null = null;
    private resetPromise: Promise<void>|null = null;
    private initState = InitState.Uninitialized;

    private constructor(
        private readonly form: HTMLFormElement,
        private readonly isSimple: boolean,
    ) {
        const widget = form.querySelector(TURNSTILE_WIDGET_SELECTOR);
        if (! (widget instanceof HTMLElement)) {
            throw new Error('Turnstile widget not found');
        }
        this.widget = widget;
        this.errorAlert = widget.parentElement!.querySelector<SlAlert>(TURNSTILE_ERROR_ALERT_SELECTOR);
    }

    public static create(form: HTMLFormElement, isSimple: boolean) {
        const instance = new FormRegistration(form, isSimple);
        instance.initPromise = instance.init();
        return instance;
    }

    private async init() {
        if (this.initPromise !== null) {
            return this.initPromise;
        }

        try {
            this.initPromise = this.initImpl();
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    private async initImpl() {
        if (this.initState !== InitState.Uninitialized) {
            throw new Error(`Cannot initialize Turnstile widget. The initState must be ${InitState.Uninitialized}, but is currently ${this.initState}.`);
        }

        this.initState = InitState.Initializing;

        setSubmitTriggersEnabled(this.form, false);

        this.widget.style.display = 'block';
        if (this.errorAlert !== null) {
            this.errorAlert.open = false;
        }

        if (this.isSimple) {
            const submitListener = (event: SubmitEvent) => {
                if (! hasResponseForForm(this.form)) {
                    event.preventDefault();
                }
            };
            this.form.addEventListener('submit', submitListener);
            this.listeners.push({target: this.form, type: 'submit', listener: submitListener});
        }

        try {
            let turnstile = this.turnstile;
            if (turnstile === null) {
                turnstile = await withTimeout(getTurnstile(), TURNSTILE_LOAD_TIMEOUT_MS);
                this.turnstile = turnstile;
            }

            if (this.initState as InitState !== InitState.Initializing) {
                return;
            }

            const widgetId = turnstile.render(this.widget, {
                callback: () => {
                    setSubmitTriggersEnabled(this.form, true);
                },
                "error-callback": () => {
                    if (! this.form.isConnected) {
                        /**
                         * Wenn das Formular nicht im DOM eingebunden ist, dann sollte
                         * das Turnstile-Widget wahrscheinlich auch entfernt werden.
                         * Ansonsten würde das Turnstile-Widget im Hintergrund weiter-
                         * arbeiten und weitere irrelevante Fehler werfen.
                         * Mit `queueMicrotask` wird das Widget nach dem aktuellen Callback
                         * entfernt.
                         */
                        queueMicrotask(() => {
                            turnstile!.remove(this.widget);
                        });
                        return true;
                    }
                },
            });

            if (widgetId === undefined || widgetId === null) {
                throw new Error('Turnstile widget could not be rendered: render function returned null or undefined.');
            }

            this.initState = InitState.Initialized;
        } catch (error) {
            this.widget.style.display = 'none';
            if (this.errorAlert !== null) {
                this.errorAlert.open = true;
            }
            throw error;
        }
    }

    public hasResponse(): boolean {
        return hasResponseForForm(this.form);
    }

    public async destroy() {
        if (this.destroyPromise !== null) {
            return this.destroyPromise;
        }

        try {
            this.destroyPromise = this.destroyImpl();
            await this.destroyPromise;
        } finally {
            this.destroyPromise = null;
        }
    }

    private async destroyImpl() {
        this.assertNotPoisoned();

        this.initState = InitState.Destroying;
        try {
            for (const listener of this.listeners) {
                listener.target.removeEventListener(listener.type, listener.listener);
            }
            this.listeners.length = 0;

            try {
                await this.initPromise;
            } catch (e) {
                // We do not care.
            }

            if (this.turnstile !== null) {
                this.turnstile.remove(this.widget);
            }
            this.initState = InitState.Uninitialized;
        } catch (e) {
            this.initState = InitState.Poisoned;
        }
    }

    public async reset() {
        if (this.resetPromise !== null) {
            return this.resetPromise;
        }

        try {
            this.resetPromise = this.resetImpl();
            await this.resetPromise;
        } finally {
            this.resetPromise = null;
        }
    }

    private async resetImpl() {
        if (this.initState === InitState.Initialized) {
            this.turnstile!.reset(this.widget);
        } else {
            await this.destroy();
            this.initPromise = this.init();
            await this.initPromise;
        }
    }

    private assertNotPoisoned() {
        if (this.initState === InitState.Poisoned) {
            throw new Error('Turnstile widget is poisoned. Cannot be used anymore.');
        }
    }
}

function setSubmitTriggersEnabled(form: HTMLFormElement, enabled: boolean): void {
    const submitTriggers = queryFormSubmitElements(form);
    for (const submitTrigger of submitTriggers) {
        submitTrigger.toggleAttribute('disabled', ! enabled);
    }
}

function hasResponseForForm(form: HTMLFormElement): boolean {
    return getResponseForForm(form) !== null;
}

function getResponseForForm(form: HTMLFormElement): string|null {
    const input = form.elements.namedItem(TURNSTILE_RESPONSE_FIELD);
    if (! (input instanceof HTMLInputElement)) {
        return null;
    }
    if (input.value === '') {
        return null;
    }
    return input.value;
}

let turnstilePromise: Promise<Turnstile>|null = null;
function getTurnstile(): Promise<Turnstile> {
    if (turnstilePromise !== null) {
        return turnstilePromise;
    }
    if ('turnstile' in window && typeof window.turnstile !== 'undefined') {
        turnstilePromise = Promise.resolve(window.turnstile as Turnstile);

        return turnstilePromise;
    }
    turnstilePromise = new Promise<Turnstile>((resolve, reject) => {
        (window as any).cloudflareTurnstileOnload = () => {
            if ('turnstile' in window && typeof window.turnstile !== 'undefined') {
                resolve(window.turnstile as Turnstile);
            } else {
                reject(new Error('Cloudflare init callback cloudflareTurnstileOnload was called but window.turnstile does not exist.'));
            }
        }
    });

    return turnstilePromise;
}

type Turnstile = {
    render: (
        widget: HTMLElement,
        options: TurnstileRenderOptions,
    ) => string|undefined|null,
    remove: (widget: HTMLElement) => void,
    reset: (widget: HTMLElement) => void
};

type TurnstileRenderOptions = {
    callback: () => void,
    "error-callback": (errorCode: string) => void,
};

type ListenerRegistration = {
    target: EventTarget,
    type: string,
    listener: (...args: any[]) => any,
}
